import { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, CircleMarker, Polygon, Polyline, Tooltip as LTooltip, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { opApi, useOperator } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import AddFieldMap from "@/components/AddFieldMap";
import { AlertTriangle, MapPin, Plus, Trash2, Users, Loader2, X, Truck } from "lucide-react";

// Fix iOS Safari + Leaflet "blank tiles in a flex parent" bug.
// When the MapContainer mounts inside a flex-1 div that briefly has 0px height
// (which iOS Safari measures at first paint), Leaflet caches that 0 and never
// requests tiles. We must call invalidateSize() once the parent actually has
// dimensions, then again on window resize/orientation change.
function MapReady() {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    // Multiple passes catch iOS Safari's late layout, the dynamic toolbar,
    // and orientation flips.
    const ids = [50, 250, 600, 1500].map(ms => setTimeout(fix, ms));
    window.addEventListener("resize", fix);
    window.addEventListener("orientationchange", fix);
    return () => {
      ids.forEach(clearTimeout);
      window.removeEventListener("resize", fix);
      window.removeEventListener("orientationchange", fix);
    };
  }, [map]);
  return null;
}

const KPH_TO_MPH = 0.621371;
const POLL_MS    = 8000;    // refresh others' positions every 8s
const HEARTBEAT_MS = 10000; // re-post our own position at least every 10s even when stationary

function FitAll({ positions, places, target }) {
  const map = useMap();
  useEffect(() => {
    if (target) { map.setView([target.lat, target.lon], 15); return; }
    const pts = [
      ...positions.map(p => [p.lat, p.lon]),
      ...places.map(p => [p.lat, p.lon]),
    ];
    if (pts.length === 0) return;
    if (pts.length === 1) map.setView(pts[0], 14);
    else map.fitBounds(pts, { padding: [40, 40], maxZoom: 16 });
  }, [target, positions.length, places.length]); // eslint-disable-line
  return null;
}

// AI-rendered 3D isometric icons of the actual machinery (Gemini Nano Banana).
// Files live at /app/frontend/public/silage-icons/*.png and are served from /silage-icons/*.png
const ROLES = {
  chopper:   { label: "Chopper",          src: "/silage-icons/chopper.png"   },
  carter:    { label: "Tractor + trailer", src: "/silage-icons/carter.png"    },
  raker:     { label: "Tractor + rake",   src: "/silage-icons/raker.png"     },
  buckraker: { label: "Buckrake",         src: "/silage-icons/buckraker.png" },
  mower:     { label: "Mowing",           src: "/silage-icons/mower.png"     },
  tractor:   { label: "Tractor",          src: "/silage-icons/tractor.png"   },
};

// Preload the role icons once so Android WebView/Chrome has them ready when
// Leaflet swaps divIcon innerHTML in. Without this, the <img> embedded in a
// divIcon sometimes never paints on Android until the marker is re-rendered.
if (typeof window !== "undefined") {
  Object.values(ROLES).forEach(r => { const i = new Image(); i.src = r.src; });
}

// FieldOps-style top-down marker: circular ring + roof-view machine icon rotated by heading.
// IMPORTANT: we render the machine artwork as a CSS background-image on the inner
// circle (not an <img> tag). On Android Chrome and Android System WebView, an
// <img> injected via Leaflet divIcon innerHTML can fail to paint until the next
// layout pass — leaving operators showing as just the small initials badge.
// background-image is processed during the same paint pass and is rock solid.
function operatorIcon(initials, isMe, stale = false, role = "tractor", heading = null) {
  const src = (ROLES[role] || ROLES.tractor).src;
  const ringColour = stale ? "#94a3b8" : (isMe ? "#10b981" : "#c41e3a");
  const opacity = stale ? 0.55 : 1;
  const rot = heading != null && !isNaN(heading) ? heading : 0;
  const showHeadingTick = heading != null && !isNaN(heading) && !stale;
  // 72 px circular badge, 56 px inner icon, rotation applied only to the icon (ring + initials stay upright).
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:72px;height:72px;opacity:${opacity}">
      <div style="position:absolute;top:0;left:0;right:0;bottom:0;border-radius:50%;background:rgba(255,255,255,.95);
                  border:3px solid ${ringColour};box-shadow:0 2px 8px rgba(0,0,0,.45);overflow:hidden">
        <div style="position:absolute;top:8px;left:8px;width:56px;height:56px;
                    background-image:url('${src}');background-size:contain;background-position:center;background-repeat:no-repeat;
                    transform:rotate(${rot}deg);transform-origin:center center;
                    transition:transform .8s ease-out;pointer-events:none;
                    -webkit-backface-visibility:hidden;backface-visibility:hidden"></div>
      </div>
      ${showHeadingTick ? `
        <div style="position:absolute;left:50%;top:-2px;width:0;height:0;
                    transform:translateX(-50%) rotate(${rot}deg);transform-origin:50% 38px;
                    border-left:7px solid transparent;border-right:7px solid transparent;
                    border-bottom:10px solid ${ringColour};transition:transform .8s ease-out"></div>` : ""}
      ${isMe && !stale ? `<div style="position:absolute;top:-6px;left:-6px;right:-6px;bottom:-6px;border:2px solid ${ringColour};border-radius:50%;animation:pulseRing 1.6s ease-out infinite"></div>` : ""}
      <div style="position:absolute;left:-2px;bottom:-2px;width:24px;height:24px;border-radius:50%;
                  background:${ringColour};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:900;
                  font-size:10px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${initials}</div>
      ${stale ? '<div style="position:absolute;right:-2px;top:-2px;width:14px;height:14px;background:#94a3b8;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:9px;font-weight:900">z</div>' : ""}
    </div>`,
    iconSize: [72, 72], iconAnchor: [36, 36],
  });
}
function placeIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:#fbbf24;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);display:grid;place-items:center"><span style="transform:rotate(45deg);color:#1a1a5c;font-weight:900;font-size:14px">⇄</span></div>`,
    iconSize: [30, 30], iconAnchor: [15, 30],
  });
}

function ClickToAdd({ onAdd, enabled }) {
  useMapEvents({
    click(e) { if (enabled) onAdd(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

export default function SilageTracker() {
  const { operator } = useOperator();
  const isAdmin = !!operator?.is_admin;
  const isSpectator = operator?.role === "spectator";
  const [data, setData] = useState({ positions: [], passing_places: [], alerts: [], my_near_place: null });
  const [filterId, setFilterId] = useState("all");
  const [addMode, setAddMode]   = useState(false);
  const [newPin, setNewPin]     = useState(null);
  const [pinName, setPinName]   = useState("");
  const [loading, setLoading]   = useState(true);
  // Restore last session's picks from localStorage so re-entering is fast
  const [myRole, setMyRole]     = useState(() => localStorage.getItem("silage:role") || "tractor");
  const [myClient, setMyClient] = useState(() => {
    try { return JSON.parse(localStorage.getItem("silage:client") || "null"); } catch { return null; }
  });
  const [myField, setMyField]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("silage:field") || "null"); } catch { return null; }
  });
  const myRoleRef   = useRef(myRole);
  const myClientRef = useRef(myClient);
  const myFieldRef  = useRef(myField);
  useEffect(() => { myRoleRef.current   = myRole;   }, [myRole]);
  useEffect(() => { myClientRef.current = myClient; }, [myClient]);
  useEffect(() => { myFieldRef.current  = myField;  }, [myField]);

  // Setup wizard — every entry starts at "role". Each step persists + advances.
  // "done" closes the wizard and reveals the map.
  // Spectators (Office/TV view-only) skip the whole wizard and go straight to "done".
  const [setupStep, setSetupStep] = useState(isSpectator ? "done" : "role"); // role | client | field | done

  // Loaded lazily by the wizard
  const [clientsList, setClientsList] = useState(null);
  const [fieldsList, setFieldsList]   = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [showAddFieldMap, setShowAddFieldMap] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // Wizard is now a SINGLE step: pick role → straight to the map. Customer
  // and field selection have been removed at the operator's request (per
  // session 2026-02-20). The dependent loaders (clients / fields) are no
  // longer triggered here; pickClient/pickField below remain for the
  // back-navigation and admin-add-field flows but are unreachable from
  // normal operator use.
  // ──────────────────────────────────────────────────────────────────────────
  const pickRole = (r) => {
    if (setupStep !== "role") return;       // double-tap / late-tap guard
    setMyRole(r);
    localStorage.setItem("silage:role", r);
    setPickerSearch("");
    setSetupStep("done");                    // ← straight to map, no customer/field
    poll();                                  // kick a fetch so the map populates immediately
    // background: persist role on the server
    opApi.post("/app/tracker/role", { role: r }).catch(() => {});
  };

  const pickClient = (c) => {
    if (setupStep !== "client") return;     // double-tap / late-tap guard
    setMyClient(c);
    localStorage.setItem("silage:client", JSON.stringify(c));
    setPickerSearch("");
    setFieldsList(null);                    // reset so step 3 shows the spinner immediately
    setSetupStep("field");                   // ← advance FIRST, no awaiting
    // background: load FieldOps + manual fields, merge, de-dupe
    const reqs = [opApi.get(`/app/tracker/fields?client_id=${encodeURIComponent(c.id)}`)];
    if (c.source === "fieldops" || (c.id || "").startsWith("fieldops:")) {
      reqs.push(opApi.get(`/app/tracker/fieldops-fields?grower=${encodeURIComponent(c.name)}`));
    }
    Promise.all(reqs)
      .then(results => {
        const merged = results.flatMap(r => r.data || []);
        // De-dupe by id (FieldOps ids are prefixed with "fieldops:" so they can't collide with local)
        const seen = new Set();
        const dedup = merged.filter(f => seen.has(f.id) ? false : (seen.add(f.id), true));
        setFieldsList(dedup);
      })
      .catch(() => setFieldsList([]));
  };

  const pickField = (f) => {
    setMyField(f);
    localStorage.setItem("silage:field", JSON.stringify(f));
    setSetupStep("done");
    poll();
  };

  // Called by AddFieldMap when admin saves a brand-new field
  const onFieldCreated = (f) => {
    setFieldsList(prev => [...(prev || []), f]);
    setShowAddFieldMap(false);
    pickField(f);
  };

  const changeRole = (r) => pickRole(r);    // legacy alias
  const [gpsError, setGpsError] = useState(null);
  const [gpsOk, setGpsOk]       = useState(false);
  const [wakeOn, setWakeOn]     = useState(false);
  const pollTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const watchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  // Last good fix — used to compute speed client-side when iOS Safari returns null for coords.speed
  const lastFixRef = useRef(null);

  // Haversine distance in metres
  const haversineM = (a, b) => {
    const R = 6371000;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
    const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon/2)**2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  // Compass bearing from point A to B, in degrees clockwise from north (0–360).
  const bearingDeg = (a, b) => {
    const toRad = d => d * Math.PI / 180;
    const toDeg = r => r * 180 / Math.PI;
    const φ1 = toRad(a.lat), φ2 = toRad(b.lat);
    const Δλ = toRad(b.lon - a.lon);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
  };

  // Poll positions — server filters passing places by the selected client so we only
  // see the relevant pins for today's job.
  const poll = async () => {
    try {
      const cid = myClientRef.current?.id;
      const url = cid ? `/app/tracker/positions?client_id=${encodeURIComponent(cid)}` : "/app/tracker/positions";
      const r = await opApi.get(url);
      setData(r.data || { positions: [], passing_places: [], alerts: [] });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  // Post a fix (used by both watchPosition and the stationary heartbeat)
  const postFix = (pos) => {
    setGpsOk(true); setGpsError(null);
    const now = Date.now();
    const lat = pos.coords.latitude;
    const lon = pos.coords.longitude;
    // iOS Safari getCurrentPosition() returns null for coords.speed; watchPosition normally does fill it.
    // Either way we compute a client-side fallback from the previous fix so the operator always sees mph.
    let speedKph = pos.coords.speed != null && pos.coords.speed >= 0 ? pos.coords.speed * 3.6 : null;
    let heading  = pos.coords.heading != null && !isNaN(pos.coords.heading) ? pos.coords.heading : null;
    const prev = lastFixRef.current;
    if (prev) {
      const dt = (now - prev.t) / 1000;
      const dm = haversineM(prev, { lat, lon });
      if (speedKph == null) {
        if (dt > 1 && dt < 60 && dm >= 2) speedKph = (dm / dt) * 3.6;
        else if (dt > 1 && dm < 2) speedKph = 0;
      }
      // Compute heading only when we've moved enough that the bearing is meaningful (>3 m).
      // Otherwise stick with the last known heading from the previous fix.
      if (heading == null) {
        if (dm >= 3) heading = bearingDeg(prev, { lat, lon });
        else heading = prev.heading != null ? prev.heading : null;
      }
    }
    lastFixRef.current = { lat, lon, t: now, heading };
    opApi.post("/app/tracker/position", {
      lat, lon,
      heading,
      speed_kph: speedKph,
      accuracy_m: pos.coords.accuracy,
      role: myRoleRef.current,
      client_id:   myClientRef.current?.id   || null,
      client_name: myClientRef.current?.name || null,
      field_id:    myFieldRef.current?.id    || null,
      field_name:  myFieldRef.current?.name  || null,
    }).catch(() => {});
  };

  const onGpsError = (err) => {
    setGpsOk(false);
    if (err.code === 1)      setGpsError("Location permission denied — enable it in Safari Settings → Privacy → Location → Allow for this site.");
    else if (err.code === 2) setGpsError("GPS unavailable — are you indoors with no signal?");
    else if (err.code === 3) setGpsError("GPS timed out — try again outside.");
    else                     setGpsError("GPS error: " + err.message);
  };

  // Heartbeat: even when watchPosition is quiet (tractor stopped at clamp), re-post the last fix
  // every HEARTBEAT_MS so the server keeps the operator "fresh" and never flips to stale.
  const heartbeat = () => {
    const prev = lastFixRef.current;
    if (!prev) return;
    opApi.post("/app/tracker/position", {
      lat: prev.lat, lon: prev.lon,
      heading: null, speed_kph: 0, accuracy_m: null,
      role: myRoleRef.current,
      client_id:   myClientRef.current?.id   || null,
      client_name: myClientRef.current?.name || null,
      field_id:    myFieldRef.current?.id    || null,
      field_name:  myFieldRef.current?.name  || null,
    }).catch(() => {});
  };

  const startWatch = () => {
    if (!navigator.geolocation) { setGpsError("This browser doesn't support GPS."); return; }
    if (watchIdRef.current != null) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      postFix, onGpsError,
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 5000 }
    );
    // Kick once immediately so the first fix arrives quickly
    navigator.geolocation.getCurrentPosition(postFix, onGpsError,
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 });
  };

  // Wake Lock — keeps the iPad screen on while the page is visible so iOS doesn't pause JS timers.
  // Supported on Safari since iOS 16.4. Re-acquired on visibilitychange because iOS auto-releases it.
  const acquireWakeLock = async () => {
    if (!("wakeLock" in navigator)) return;
    try {
      const wl = await navigator.wakeLock.request("screen");
      wakeLockRef.current = wl;
      setWakeOn(true);
      wl.addEventListener("release", () => setWakeOn(false));
    } catch { setWakeOn(false); }
  };

  // Manual retry button — also re-arms wake lock + watch
  const reportMine = () => {
    startWatch();
    acquireWakeLock();
  };

  useEffect(() => {
    poll();
    pollTimer.current = setInterval(poll, POLL_MS);

    // Spectators (Office TV) just watch — no GPS, no wake-lock, no heartbeat.
    if (!isSpectator) {
      startWatch();
      acquireWakeLock();
      heartbeatTimer.current = setInterval(heartbeat, HEARTBEAT_MS);
    }

    // Re-arm GPS + Wake Lock the moment Safari returns to foreground
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        if (!isSpectator) {
          startWatch();
          acquireWakeLock();
        }
        poll();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(pollTimer.current);
      clearInterval(heartbeatTimer.current);
      document.removeEventListener("visibilitychange", onVisible);
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
    };
  }, []); // eslint-disable-line

  const visible = useMemo(() =>
    filterId === "all" ? data.positions : data.positions.filter(p => p.operator_id === filterId),
    [filterId, data.positions]);

  const target = filterId === "all" ? null : visible[0] || null;

  const addPlace = async () => {
    if (!newPin || !pinName.trim()) return;
    try {
      await opApi.post("/app/tracker/passing-places", {
        name: pinName.trim(), lat: newPin.lat, lon: newPin.lon, notes: "",
      });
      setNewPin(null); setPinName(""); setAddMode(false);
      poll();
    } catch (e) { window.alert(e.response?.data?.detail || "Failed"); }
  };
  const deletePlace = async (p) => {
    if (!window.confirm(`Delete passing place "${p.name}"?`)) return;
    try { await opApi.delete(`/app/tracker/passing-places/${p.id}`); poll(); }
    catch { window.alert("Couldn't delete"); }
  };

  const fmtMph = (kph) => {
    if (kph == null) return "—";
    if (kph < 0.5) return "0.0 mph";
    return `${(kph * KPH_TO_MPH).toFixed(1)} mph`;
  };
  const fmtTimeAgo = (iso) => {
    if (!iso) return "—";
    const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s/60)}m ago`;
    return `${Math.round(s/3600)}h ago`;
  };

  return (
    <div
      className="bg-slate-50 flex flex-col overflow-hidden"
      style={{ height: "100dvh", minHeight: "-webkit-fill-available" }}
    >
      <style>{`@keyframes pulseRing { 0% { transform:scale(1); opacity:.8 } 100% { transform:scale(2); opacity:0 } } .leaflet-container { background:#1e293b; }`}</style>
      <OperatorHeader back="/app" title="Silage Tracker" />

      {/* Admin-only: full-screen "Add field" map for snap/corners/circle boundary */}
      {showAddFieldMap && (
        <AddFieldMap
          client={myClient}
          defaultCentre={lastFixRef.current ? [lastFixRef.current.lat, lastFixRef.current.lon] : null}
          onCancel={() => setShowAddFieldMap(false)}
          onSaved={onFieldCreated}
        />
      )}

      {/* Setup wizard — single step: Role, then the map appears */}
      {setupStep !== "done" && (
        <div className="fixed inset-0 z-[5000] bg-slate-900/85 backdrop-blur-sm grid place-items-center px-4"
             data-testid="setup-wizard">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[92vh] flex flex-col">
            <div className="bg-[#1a1a5c] text-white px-6 py-4 shrink-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 flex items-center gap-2">
                Silage tracker
              </div>
              <div className="font-display font-black text-2xl mt-1">
                What are you driving?
              </div>
            </div>

            {/* STEP 1 — role */}
            {setupStep === "role" && (
              <div className="p-4 grid grid-cols-2 gap-3 overflow-y-auto">
                {Object.entries(ROLES).map(([key, r]) => {
                  const isSaved = key === myRole;
                  return (
                    <button
                      key={key}
                      onClick={() => pickRole(key)}
                      data-testid={`role-pick-${key}`}
                      className={`flex flex-col items-center gap-2 rounded-xl p-4 border-2 transition ${isSaved
                        ? "bg-amber-50 border-amber-400 ring-2 ring-amber-300"
                        : "bg-slate-50 border-slate-200 hover:bg-amber-100 hover:border-amber-400"}`}
                    >
                      <img src={r.src} alt="" className="h-12 w-auto" />
                      <span className="font-bold text-sm text-[#1a1a5c]">{r.label}</span>
                      {isSaved && <span className="text-[10px] uppercase tracking-widest text-amber-700">Last used</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* STEP 2 — client */}
            {setupStep === "client" && (
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} autoFocus
                       placeholder="Search customers…" data-testid="client-search"
                       className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-[#1a1a5c] focus:outline-none focus:border-[#1a1a5c]" />
                {!clientsList && (
                  <div className="text-slate-500 text-sm flex items-center gap-2 py-4 justify-center">
                    <Loader2 className="animate-spin" size={14} /> Loading customers…
                  </div>
                )}
                {clientsList && (clientsList
                  .filter(c => !pickerSearch || (c.name || "").toLowerCase().includes(pickerSearch.toLowerCase()))
                  .map(c => {
                    const isSaved = myClient?.id === c.id;
                    return (
                      <button key={c.id} onClick={() => pickClient(c)} data-testid={`client-pick-${c.id}`}
                              className={`w-full text-left rounded-xl px-4 py-3 border transition flex items-center gap-3 ${isSaved
                                ? "bg-amber-50 border-amber-400" : "bg-white border-slate-200 hover:bg-amber-50 hover:border-amber-300"}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-[#1a1a5c]">{c.name}</div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-2">
                            {c.field_count != null && <span>{c.field_count} field{c.field_count === 1 ? "" : "s"}</span>}
                            {c.source === "fieldops" && <span className="bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded">FieldOps</span>}
                          </div>
                        </div>
                        {isSaved && <span className="text-[10px] uppercase tracking-widest text-amber-700">Last used</span>}
                      </button>
                    );
                  })
                )}
                {clientsList && clientsList.length === 0 && (
                  <div className="text-slate-500 text-sm text-center py-6">
                    No customers set up yet — {isAdmin ? "open Admin → Fields to add the first one." : "ask Andy or Steven to set them up."}
                  </div>
                )}
                {isAdmin && (
                  <button onClick={() => pickClient({ id: "manual", name: "Manual customer" })}
                          data-testid="client-pick-manual"
                          className="w-full text-center text-sm text-[#c41e3a] font-bold py-3 hover:bg-rose-50 rounded-xl border-2 border-dashed border-slate-200 hover:border-rose-300 mt-2">
                    Skip customer / generic
                  </button>
                )}
              </div>
            )}

            {/* STEP 3 — field */}
            {setupStep === "field" && (
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} autoFocus
                       placeholder="Search fields…" data-testid="field-search"
                       className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-[#1a1a5c] focus:outline-none focus:border-[#1a1a5c]" />

                {!fieldsList && (
                  <div className="text-slate-500 text-sm flex items-center gap-2 py-4 justify-center">
                    <Loader2 className="animate-spin" size={14} /> Loading fields…
                  </div>
                )}

                {fieldsList && fieldsList
                  .filter(f => !pickerSearch || (f.name || "").toLowerCase().includes(pickerSearch.toLowerCase()))
                  .map(f => (
                    <button key={f.id} onClick={() => pickField(f)} data-testid={`field-pick-${f.id}`}
                            className="w-full text-left bg-white hover:bg-amber-50 hover:border-amber-400 border border-slate-200 rounded-xl px-4 py-3 transition flex items-center gap-3">
                      <MapPin size={16} className="text-[#c41e3a] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[#1a1a5c] truncate">{f.name}</div>
                        <div className="text-[10px] text-slate-500">{f.lat.toFixed(4)}, {f.lon.toFixed(4)}</div>
                      </div>
                    </button>
                  ))
                }

                {fieldsList && fieldsList.length === 0 && (
                  <div className="text-slate-500 text-sm text-center py-3">No fields saved for this customer yet — drop a new pin below.</div>
                )}

                {/* Admin-only: drop a new pin on the satellite map (snap / corners / circle) */}
                {isAdmin && (
                  <div className="border-t border-slate-200 mt-3 pt-3">
                    <button onClick={() => setShowAddFieldMap(true)} data-testid="open-add-field-map"
                            className="w-full bg-[#1a1a5c] hover:bg-[#11113d] text-white font-bold rounded-lg py-3 text-sm flex items-center justify-center gap-2">
                      <MapPin size={14} /> Add new field on map
                    </button>
                    <div className="text-[10px] text-slate-400 text-center mt-1.5">
                      Tap the satellite map to drop a pin, then snap / draw / size the boundary.
                    </div>
                  </div>
                )}

                {!isAdmin && fieldsList && fieldsList.length === 0 && (
                  <div className="border-t border-slate-200 mt-3 pt-3 text-[11px] text-center text-slate-500">
                    No fields configured for this customer yet. Ask Andy or Steven to set them up in admin.
                  </div>
                )}

                <button onClick={() => pickField({ id: null, name: null })}
                        data-testid="field-skip"
                        className="w-full text-center text-sm text-slate-500 hover:text-[#c41e3a] py-3 mt-2">
                  Skip — no specific field
                </button>
              </div>
            )}

            {/* Back nav (steps 2 & 3) */}
            {setupStep !== "role" && (
              <div className="px-4 py-2 border-t border-slate-200 shrink-0">
                <button onClick={() => { setPickerSearch(""); setSetupStep(setupStep === "field" ? "client" : "role"); }}
                        data-testid="setup-back"
                        className="text-xs text-slate-500 hover:text-[#1a1a5c] font-bold">
                  ← Back
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alert banner */}
      {data.alerts && data.alerts.filter(a => a.involves_me).map(a => (
        <div key={a.place_id} className="bg-[#c41e3a] text-white px-4 py-3 flex items-center gap-3 animate-pulse">
          <AlertTriangle size={22} />
          <div className="flex-1">
            <div className="font-display font-black">⚠ PULL IN AT "{a.place_name}"</div>
            <div className="text-xs text-white/90">
              {a.operators.filter(o => !o.is_me).map(o => o.name).join(", ")} {a.operators.length > 2 ? "are" : "is"} also near this passing place
            </div>
          </div>
        </div>
      ))}
      {!data.alerts?.some(a => a.involves_me) && data.my_near_place && (
        <div className="bg-amber-400 text-[#1a1a5c] px-4 py-2 flex items-center gap-2 text-sm">
          <MapPin size={14} /> Approaching passing place <strong>{data.my_near_place.name}</strong> ({data.my_near_place.distance_m}m)
        </div>
      )}

      {/* GPS status banner */}
      {!isSpectator && gpsError && (
        <div className="bg-amber-500/90 text-[#1a1a5c] px-4 py-3 flex items-center gap-3">
          <AlertTriangle size={18} />
          <div className="flex-1 text-sm">{gpsError}</div>
          <button onClick={reportMine} className="bg-[#1a1a5c] text-white text-xs font-bold px-3 py-1.5 rounded">
            Retry
          </button>
        </div>
      )}
      {!isSpectator && !gpsError && !gpsOk && (
        <div className="bg-slate-100 text-slate-700 px-4 py-2 text-xs flex items-center gap-2">
          <Loader2 className="animate-spin" size={12} /> Getting your location…
        </div>
      )}
      {!isSpectator && gpsOk && (
        <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-800 px-4 py-1.5 text-[11px] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Live tracking on
          {wakeOn && <span className="ml-2 text-emerald-700">· screen kept awake</span>}
          {!wakeOn && "wakeLock" in navigator && (
            <button onClick={reportMine} className="ml-2 underline">
              tap to keep screen on
            </button>
          )}
        </div>
      )}

      {isSpectator && (
        <div className="bg-[#1a1a5c] text-white px-4 py-2 text-[11px] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
          <strong>Office view</strong> · watching live fleet (no GPS broadcast from this device)
        </div>
      )}

      {/* Controls */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 flex-wrap shadow-sm">
        <Users size={16} className="text-[#1a1a5c]" />
        <select value={filterId} onChange={e => setFilterId(e.target.value)} data-testid="tracker-filter"
                className="bg-slate-100 border border-slate-300 rounded px-2 py-1 text-sm">
          <option value="all">Show all ({data.positions.length})</option>
          {data.positions.map(p => (
            <option key={p.operator_id} value={p.operator_id}>{p.operator_name}</option>
          ))}
        </select>
        <div className="flex-1" />
        {!isSpectator && (
          <button onClick={() => setAddMode(!addMode)} data-testid="add-place-toggle"
                  className={`text-sm font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 ${addMode ? "bg-[#c41e3a] text-white" : "bg-amber-400 text-[#1a1a5c]"}`}>
            {addMode ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Add passing place</>}
          </button>
        )}
      </div>

      {addMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-[#1a1a5c]">
          📍 Tap the map at the passing place location
        </div>
      )}

      <div className="flex-1 relative" style={{ minHeight: 360 }}>
        {loading && (
          <div className="absolute inset-0 grid place-items-center bg-slate-50 z-[1000]">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        )}
        <MapContainer center={[57.243, -2.495]} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%", minHeight: 360 }}>
          <MapReady />
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='Tiles © Esri' maxZoom={19}
          />
          {/* Roads on top of the satellite — Esri "Hybrid" composition */}
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19} opacity={0.9}
          />
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19} opacity={0.85}
          />
          <FitAll positions={visible} places={data.passing_places} target={target} />
          <ClickToAdd enabled={addMode} onAdd={(lat, lon) => setNewPin({ lat, lon })} />

          {/* Passing places */}
          {data.passing_places.map(p => (
            <Marker key={p.id} position={[p.lat, p.lon]} icon={placeIcon()}>
              <Popup>
                <div className="text-sm">
                  <div className="font-bold text-[#1a1a5c]">{p.name}</div>
                  {p.notes && <div className="text-xs text-slate-600 mt-1">{p.notes}</div>}
                  <button onClick={() => deletePlace(p)} className="mt-2 text-xs text-[#c41e3a] flex items-center gap-1">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* This customer's saved field outlines (green) — only shown after a client is picked */}
          {myClient && (fieldsList || []).filter(f => f.polygon && f.polygon.length >= 3).map(f => (
            <Polygon key={`field-${f.id}`} positions={f.polygon}
                     pathOptions={{ color: myField?.id === f.id ? "#f5a623" : "#10b981",
                                    weight: myField?.id === f.id ? 3 : 2,
                                    fillOpacity: myField?.id === f.id ? 0.25 : 0.1 }} />
          ))}

          {/* Breadcrumb trails — what each operator has driven in the last 90 min */}
          {visible.filter(p => p.trail && p.trail.length >= 2).map(p => (
            <Polyline
              key={`trail-${p.operator_id}`}
              positions={p.trail}
              pathOptions={{
                color:       p.is_me ? "#10b981" : "#c41e3a",
                weight:      p.is_me ? 5 : 4,
                opacity:     p.stale ? 0.35 : 0.85,
                lineCap:     "round",
                lineJoin:    "round",
                dashArray:   p.stale ? "6 6" : null,
              }}
            />
          ))}

          {/* Operators */}
          {visible.map(p => (
            <Marker key={p.operator_id} position={[p.lat, p.lon]} icon={operatorIcon(p.initials, p.is_me, p.stale, p.role, p.heading)}>
              <LTooltip permanent direction="top" offset={[0, -22]}>
                <div className="text-xs" style={{ opacity: p.stale ? 0.7 : 1 }}>
                  <strong>{p.operator_name}{p.is_me ? " (you)" : ""}</strong>{p.stale ? " — offline" : ""}<br />
                  <span style={{ color: p.stale ? "#64748b" : "#c41e3a", fontWeight: 700 }}>
                    {p.stale ? "last seen" : fmtMph(p.speed_kph)}
                  </span> · {fmtTimeAgo(p.updated_at)}
                </div>
              </LTooltip>
            </Marker>
          ))}

          {/* Pending new passing place pin */}
          {newPin && (
            <CircleMarker center={[newPin.lat, newPin.lon]} radius={12}
                          pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.6 }}>
              <Popup>
                <input value={pinName} onChange={e => setPinName(e.target.value)}
                       placeholder="Passing place name"
                       data-testid="pin-name"
                       className="border border-slate-300 rounded px-2 py-1 text-sm w-full mb-2" />
                <div className="flex gap-1">
                  <button onClick={() => setNewPin(null)}
                          className="text-xs px-2 py-1 bg-slate-200 rounded">Cancel</button>
                  <button onClick={addPlace} disabled={!pinName.trim()} data-testid="pin-save"
                          className="text-xs px-2 py-1 bg-emerald-500 text-white rounded disabled:opacity-50">
                    Save
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          )}
        </MapContainer>
      </div>

      {/* Bottom legend */}
      <div className="bg-white border-t border-slate-200 px-4 py-2 text-xs text-slate-600 flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" /> You</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#c41e3a] border-2 border-white" /> Others</span>
        <span className="flex items-center gap-1"><span className="w-5 h-[3px] bg-[#c41e3a] rounded" /> Route (last 90 min)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 border border-white" style={{ transform: "rotate(45deg)" }} /> Passing place</span>
        <span className="ml-auto text-slate-400">Updates every {POLL_MS/1000}s</span>
      </div>
    </div>
  );
}
